import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import ProtectedRoute from "./components/ProtectedRoute"
import DashboardLayout from "./components/DashboardLayout"
import { Toaster } from "./components/ui/toaster"
import { apiRequest } from "./lib/api"
import Dashboard from "./pages/Dashboard"
import WorkflowBuilderPage from "./pages/WorkflowBuilder"
import WorkflowRunPage from "./pages/WorkflowRun"
import BroadcastsPage from "./pages/Broadcasts"
import WorkflowsPage from "./pages/WorkflowsPage"
import WorkflowAnalyticsPage from "./pages/WorkflowAnalytics"
import SubscribersPage from "./pages/Subscribers"
import ContactsPage from "./pages/Contacts"
import SegmentsPage from "./pages/Segments"
import SettingsPage from "./pages/Settings"
import MembersPage from "./pages/Members"
import RecurringBroadcastDetail from "./pages/RecurringBroadcastDetail"
import Onboarding from "./pages/Onboarding"
import ProjectsPage from "./pages/Projects"
import SignIn from "./pages/SignIn"
import SignUp from "./pages/SignUp"
import type { ApiError } from "./lib/api"
import type { Account, Project, User } from "./types"

type MeResponse = {
  user: User | null
  account?: Account
  projects?: Project[]
  current_project?: Project | null
}

const reservedRoutes = new Set(["sign-in", "sign-up", "onboarding"])

const resolveProjectSlug = (pathname: string) => {
  const segment = pathname.split("/").filter(Boolean)[0]
  if (!segment || reservedRoutes.has(segment)) return null
  return segment
}

const App = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const projectSlug = resolveProjectSlug(location.pathname)

  const { data, isLoading, isError } = useQuery<MeResponse, ApiError>({
    queryKey: ["me", projectSlug],
    queryFn: () => {
      const path = projectSlug
        ? `/api/me?project_slug=${encodeURIComponent(projectSlug)}`
        : "/api/me"
      return apiRequest<MeResponse>(path)
    },
  })

  const handleAuth = (nextUser: User | null) => {
    queryClient.setQueriesData({ queryKey: ["me"] }, { user: nextUser })
    queryClient.invalidateQueries({ queryKey: ["me"] })
  }

  const signOutMutation = useMutation<unknown, ApiError>({
    mutationFn: () => apiRequest("/api/sign_out", { method: "DELETE" }),
    onSettled: () => {
      queryClient.setQueriesData({ queryKey: ["me"] }, { user: null })
      navigate("/sign-in", { replace: true })
    },
  })

  const user = data?.user ?? null
  const loading = isLoading
  const resolvedUser = isError ? null : user
  const account = data?.account ?? null
  const projects = data?.projects ?? []
  const currentProject = data?.current_project ?? null
  const hasProjects = projects.length > 0
  const primarySlug = currentProject?.slug ?? projects[0]?.slug ?? null
  const homePath = primarySlug ? `/${primarySlug}` : "/onboarding"

  const dashboardProps = {
    user: resolvedUser as User,
    account: account!,
    projects,
    currentProject,
    onSignOut: () => signOutMutation.mutate(),
  }

  const withLayout = (children: React.ReactNode) => (
    <ProtectedRoute user={resolvedUser} loading={loading}>
      {account ? (
        <DashboardLayout {...dashboardProps}>
          {children}
        </DashboardLayout>
      ) : (
        <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
          Loading account...
        </div>
      )}
    </ProtectedRoute>
  )

  return (
    <>
      <Routes>
        <Route path="/sign-in" element={<SignIn onAuth={handleAuth} />} />
        <Route path="/sign-up" element={<SignUp onAuth={handleAuth} />} />
        <Route
          path="/"
          element={
            <ProtectedRoute user={resolvedUser} loading={loading}>
              <Navigate to={homePath} replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute user={resolvedUser} loading={loading}>
              {hasProjects ? <Navigate to={homePath} replace /> : <Onboarding />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/:projectSlug"
          element={withLayout(
            <Dashboard
              user={resolvedUser as User}
              account={account!}
              currentProject={currentProject}
            />
          )}
        />
        <Route
          path="/:projectSlug/projects"
          element={withLayout(<ProjectsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/subscribers"
          element={withLayout(<SubscribersPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/segments"
          element={withLayout(<SegmentsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/contacts"
          element={withLayout(<ContactsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/broadcasts"
          element={withLayout(<BroadcastsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/workflows"
          element={withLayout(<WorkflowsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/settings"
          element={withLayout(<SettingsPage currentProject={currentProject} />)}
        />
        <Route
          path="/:projectSlug/members"
          element={withLayout(<MembersPage account={account!} />)}
        />
        <Route
          path="/:projectSlug/broadcasts/:workflowId/recurring"
          element={withLayout(<RecurringBroadcastDetail />)}
        />
        <Route
          path="/:projectSlug/broadcasts/:workflowId/builder"
          element={withLayout(<WorkflowBuilderPage />)}
        />
        <Route
          path="/:projectSlug/workflows/:workflowId/builder"
          element={withLayout(<WorkflowBuilderPage />)}
        />
        <Route
          path="/:projectSlug/workflows/:workflowId/analytics"
          element={withLayout(<WorkflowAnalyticsPage />)}
        />
        <Route
          path="/:projectSlug/broadcasts/:workflowId/run"
          element={withLayout(<WorkflowRunPage />)}
        />
        <Route
          path="/:projectSlug/broadcasts/:workflowId/run/:runId"
          element={withLayout(<WorkflowRunPage />)}
        />
        <Route
          path="*"
          element={
            <ProtectedRoute user={resolvedUser} loading={loading}>
              <Navigate to={homePath} replace />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </>
  )
}

export default App
